function morphChildren(newParent, oldParent) {
  var _a, _b;
  let oldChild;
  let newChild;
  let offset = 0;
  for (let i = 0; ; i++) {
    oldChild = (_a = oldParent.childNodes[i]) != null ? _a : null;
    newChild = (_b = newParent.childNodes[i - offset]) != null ? _b : null;
    if (!oldChild && !newChild) break;
    if (!oldChild && newChild) {
      oldParent.appendChild(newChild);
      offset++;
      continue;
    }
    if (oldChild && !newChild) {
      oldParent.removeChild(oldChild);
      i--;
      continue;
    }
    if (oldChild && newChild) {
      if (isSameNode(newChild, oldChild)) {
        morphNode(newChild, oldChild);
        morphChildren(newChild, oldChild);
        continue;
      }
      let match = null;
      for (let j = i + 1; j < oldParent.childNodes.length; j++) {
        if (isSameNode(newChild, oldParent.childNodes[j])) {
          match = oldParent.childNodes[j];
          break;
        }
      }
      if (match) {
        morphNode(newChild, match);
        morphChildren(newChild, match);
        oldParent.insertBefore(match, oldChild);
      } else {
        if (newChild.nodeType === oldChild.nodeType && newChild.nodeType !== Node.ELEMENT_NODE) {
          morphNode(newChild, oldChild);
        } else if (newChild.nodeType === Node.ELEMENT_NODE && oldChild.nodeType === Node.ELEMENT_NODE && newChild.tagName === oldChild.tagName) {
          morphNode(newChild, oldChild);
          morphChildren(newChild, oldChild);
        } else {
          oldParent.insertBefore(newChild, oldChild);
          offset++;
        }
      }
    }
  }
}
function isSameNode(a, b) {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType === Node.ELEMENT_NODE) {
    return a.tagName === b.tagName;
  }
  return true;
}
function morphNode(newNode, oldNode) {
  if (newNode.nodeType === Node.TEXT_NODE || newNode.nodeType === Node.COMMENT_NODE) {
    if (oldNode.nodeValue !== newNode.nodeValue) {
      oldNode.nodeValue = newNode.nodeValue;
    }
    return;
  }
  if (!(newNode instanceof Element) || !(oldNode instanceof Element)) return;
  copyAttributes(newNode, oldNode);
  if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
    syncInputState(newNode, oldNode);
  } else if (newNode instanceof HTMLSelectElement && oldNode instanceof HTMLSelectElement) {
    const newValue = newNode.value;
    queueMicrotask(() => {
      if (oldNode.value !== newValue) {
        oldNode.value = newValue;
      }
    });
  } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
    if (newNode.selected !== oldNode.selected) {
      oldNode.selected = newNode.selected;
    }
  } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
    if (newNode.value !== oldNode.value) {
      oldNode.value = newNode.value;
    }
  }
}
function copyAttributes(from, to) {
  for (const attr of Array.from(from.attributes)) {
    if ((attr.name === "src" || attr.name === "href" || attr.name === "srcset") && to.getAttribute(attr.name) === attr.value) {
      continue;
    }
    if (to.getAttribute(attr.name) !== attr.value) {
      to.setAttribute(attr.name, attr.value);
    }
  }
  for (const attr of Array.from(to.attributes)) {
    if (!from.hasAttribute(attr.name)) {
      to.removeAttribute(attr.name);
    }
  }
}
function syncInputState(newInput, oldInput) {
  if (newInput.type === "file") return;
  if (newInput.checked !== oldInput.checked) {
    oldInput.checked = newInput.checked;
  }
  if (newInput.disabled !== oldInput.disabled) {
    oldInput.disabled = newInput.disabled;
  }
  if (newInput.value !== oldInput.value) {
    oldInput.value = newInput.value;
    oldInput.setAttribute("value", newInput.value);
  }
}
class SectionRenderer {
  constructor() {
    this.cache = /* @__PURE__ */ new Map();
    this.pending = /* @__PURE__ */ new Map();
    this.abortControllers = /* @__PURE__ */ new Map();
  }
  /** Costruisce URL per la Section Rendering API */
  buildURL(sectionId, url = new URL(window.location.href)) {
    url.searchParams.set("section_id", sectionId);
    url.searchParams.sort();
    return url.toString();
  }
  /** Fetch dell'HTML di una sezione (con cache e deduplicazione) */
  async fetchHTML(sectionId, useCache = true, url) {
    const sectionUrl = this.buildURL(sectionId, url != null ? url : new URL(window.location.href));
    const pendingPromise = this.pending.get(sectionUrl);
    if (pendingPromise) return pendingPromise;
    if (useCache) {
      const cached = this.cache.get(sectionUrl);
      if (cached) return cached;
    }
    const promise = fetch(sectionUrl).then((r) => {
      if (!r.ok) throw new Error(`Section fetch fallito: ${r.status} ${r.statusText}`);
      return r.text();
    });
    this.pending.set(sectionUrl, promise);
    try {
      const html = await promise;
      this.cache.set(sectionUrl, html);
      return html;
    } finally {
      this.pending.delete(sectionUrl);
    }
  }
  /** Fetch + morph della sezione nel DOM */
  async renderSection(sectionId, url) {
    const existing = this.abortControllers.get(sectionId);
    if (existing) existing.abort();
    const controller = new AbortController();
    this.abortControllers.set(sectionId, controller);
    const html = await this.fetchHTML(sectionId, false, url);
    if (controller.signal.aborted) return;
    this.abortControllers.delete(sectionId);
    const existingEl = document.getElementById(`shopify-section-${sectionId}`);
    if (!existingEl) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newEl = doc.getElementById(`shopify-section-${sectionId}`);
    if (!newEl) return;
    morphChildren(newEl, existingEl);
  }
  /** Invalida la cache per una sezione */
  invalidate(sectionId) {
    for (const key of this.cache.keys()) {
      if (key.includes(`section_id=${sectionId}`)) {
        this.cache.delete(key);
      }
    }
  }
}
function debounce(fn, ms) {
  let timer;
  const debounced = function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}
function supportsViewTransitions() {
  return typeof document.startViewTransition === "function";
}
function startViewTransition(callback) {
  if (!supportsViewTransitions()) {
    callback();
    return;
  }
  document.startViewTransition(callback);
}
class WcCollectionFilters extends HTMLElement {
  constructor() {
    super(...arguments);
    this.renderer = new SectionRenderer();
    this.updateAbort = null;
    this.renderGeneration = 0;
    // Debounce per evitare raffiche di richieste
    this.debouncedUpdate = debounce(() => this.updateFilters(), 50);
    // ---------------------------------------------------------------------------
    // Event handlers
    // ---------------------------------------------------------------------------
    this.handleChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;
      if (!target.closest("[data-filters-form]")) return;
      event.preventDefault();
      event.stopPropagation();
      this.debouncedUpdate();
    };
    this.handleClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const removeBtn = target.closest("[data-remove-filter-url]");
      if (removeBtn) {
        event.preventDefault();
        const url = removeBtn.dataset.removeFilterUrl;
        if (url) this.navigateToFilterURL(url);
        return;
      }
      const clearBtn = target.closest("[data-clear-filters]");
      if (clearBtn) {
        event.preventDefault();
        const href = clearBtn.getAttribute("href");
        if (href) this.navigateToFilterURL(href);
        return;
      }
      const clearAllBtn = target.closest("[data-clear-all-filters]");
      if (clearAllBtn) {
        event.preventDefault();
        const href = clearAllBtn.dataset.clearUrl;
        if (href) this.navigateToFilterURL(href);
        return;
      }
      const loadMoreBtn = target.closest("[data-load-more]");
      if (loadMoreBtn) {
        event.preventDefault();
        this.loadMore(loadMoreBtn);
        return;
      }
    };
    this.handlePopState = () => {
      this.renderer.invalidate(this.sectionId);
      startViewTransition(() => this.renderFilters());
    };
  }
  get sectionId() {
    var _a;
    return (_a = this.getAttribute("section-id")) != null ? _a : "";
  }
  connectedCallback() {
    var _a, _b;
    if (!this.sectionId) {
      (_a = window.LOG) == null ? void 0 : _a.error("[WcCollectionFilters] section-id mancante");
      return;
    }
    this.addEventListener("change", this.handleChange);
    this.addEventListener("click", this.handleClick);
    window.addEventListener("popstate", this.handlePopState);
    (_b = window.LOG) == null ? void 0 : _b.info("[WcCollectionFilters] Filtri asincroni attivati per sezione:", this.sectionId);
    const url = new URL(window.location.href);
    if (url.searchParams.has("page")) {
      url.searchParams.delete("page");
      history.replaceState(null, "", url.toString());
      this.renderer.invalidate(this.sectionId);
      this.renderFilters();
    } else {
      this.updateProgress();
    }
  }
  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);
    this.removeEventListener("click", this.handleClick);
    window.removeEventListener("popstate", this.handlePopState);
    this.debouncedUpdate.cancel();
  }
  // ---------------------------------------------------------------------------
  // Utilità
  // ---------------------------------------------------------------------------
  /** Parsa una stringa HTML in un Document */
  parseHTML(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }
  /**
   * Fetch + morph ristretto: aggiorna solo il contenuto di questo WC,
   * senza toccare la barra sticky o altri elementi esterni.
   * Aggiorna separatamente lo stato del bottone filtri nella sticky bar.
   */
  async renderFilters() {
    const gen = ++this.renderGeneration;
    const html = await this.renderer.fetchHTML(this.sectionId, false);
    if (gen !== this.renderGeneration) return;
    const doc = this.parseHTML(html);
    const newWc = doc.querySelector("wc-collection-filters");
    if (!newWc) return;
    morphChildren(newWc, this);
    this.syncFilterButton(doc);
    this.updateProgress();
  }
  /**
   * Sincronizza l'aspetto del bottone filtri nella sticky bar
   * copiando le classi dal nuovo HTML renderizzato dal server.
   */
  syncFilterButton(doc) {
    const newBtn = doc.querySelector("[data-filter-button]");
    const oldBtn = document.querySelector("[data-filter-button]");
    if (newBtn && oldBtn) {
      oldBtn.className = newBtn.className;
    }
  }
  // ---------------------------------------------------------------------------
  // Logica filtri
  // ---------------------------------------------------------------------------
  /** Raccoglie i parametri dal form filtri e aggiorna URL + sezione */
  async updateFilters() {
    var _a;
    if (this.updateAbort) this.updateAbort.abort();
    const abort = new AbortController();
    this.updateAbort = abort;
    this.setAttribute("aria-busy", "true");
    try {
      const form = this.querySelector("[data-filters-form]");
      if (!form) return;
      const formData = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (typeof value === "string" && value !== "") {
          params.append(key, value);
        }
      }
      params.delete("page");
      const url = new URL(window.location.href);
      url.search = params.toString();
      history.pushState({ filters: true }, "", url.toString());
      await new Promise((resolve) => {
        startViewTransition(async () => {
          await this.renderFilters();
          resolve();
        });
      });
    } catch (err) {
      if (abort.signal.aborted) return;
      (_a = window.LOG) == null ? void 0 : _a.error("[WcCollectionFilters] Errore aggiornamento filtri:", err);
    } finally {
      if (this.updateAbort === abort) {
        this.removeAttribute("aria-busy");
        this.updateAbort = null;
      }
    }
  }
  /** Naviga a un URL specifico (rimozione filtro) — morph ristretto al WC */
  async navigateToFilterURL(href) {
    var _a;
    this.setAttribute("aria-busy", "true");
    try {
      const url = new URL(href, window.location.origin);
      history.pushState({ filters: true }, "", url.toString());
      this.renderer.invalidate(this.sectionId);
      await new Promise((resolve) => {
        startViewTransition(async () => {
          await this.renderFilters();
          resolve();
        });
      });
    } catch (err) {
      (_a = window.LOG) == null ? void 0 : _a.error("[WcCollectionFilters] Errore navigazione filtro:", err);
    } finally {
      this.removeAttribute("aria-busy");
    }
  }
  // ---------------------------------------------------------------------------
  // "Carica altri" — reveal progressivo via CSS (niente fetch)
  // ---------------------------------------------------------------------------
  /**
   * "Carica altri": rivela le prossime `data-page-size` card nascoste (class `hidden`).
   * Tutte le card non scadute sono già nel DOM, ordinate globalmente per data lato server;
   * qui togliamo solo `hidden` dalle prossime — nessuna fetch, nessun cambio di URL.
   * Quando non restano card nascoste, rimuoviamo il bottone.
   */
  loadMore(button) {
    var _a, _b;
    const pageSize = parseInt((_a = button.dataset.pageSize) != null ? _a : "0", 10) || this.querySelectorAll("[data-workshop-card]").length;
    const hidden = Array.from(
      this.querySelectorAll("[data-workshop-card].hidden")
    );
    for (const card of hidden.slice(0, pageSize)) {
      card.classList.remove("hidden");
    }
    this.updateProgress();
    if (this.querySelectorAll("[data-workshop-card].hidden").length === 0) {
      (_b = button.closest("[data-load-more-wrapper]")) == null ? void 0 : _b.remove();
    }
  }
  /**
   * Aggiorna il contatore "X di Y" e la barra di avanzamento.
   * Tutte le card non scadute sono nel DOM (ordinate globalmente per data):
   * X = card visibili ([data-workshop-card]:not(.hidden)), Y = totale ([data-workshop-card]).
   * Coerente coi filtri perché il set filtrato è ri-renderizzato server-side.
   */
  updateProgress() {
    const progress = this.querySelector("[data-load-progress]");
    if (!progress) return;
    const total = this.querySelectorAll("[data-workshop-card]").length;
    const shown = this.querySelectorAll("[data-workshop-card]:not(.hidden)").length;
    const shownEl = progress.querySelector("[data-progress-shown]");
    const totalEl = progress.querySelector("[data-progress-total]");
    const barEl = progress.querySelector("[data-progress-bar]");
    if (shownEl) shownEl.textContent = String(shown);
    if (totalEl) totalEl.textContent = String(total);
    if (barEl) {
      const pct = total > 0 ? Math.round(shown / total * 100) : 0;
      barEl.style.width = `${pct}%`;
    }
  }
}
if (!customElements.get("wc-collection-filters")) {
  customElements.define("wc-collection-filters", WcCollectionFilters);
}
